"use client";

import DoneAllIcon from "@mui/icons-material/DoneAll";
import { Card } from "@mui/material";
import React from "react";

import talk2buyAvatar from "@/assets/talk2buy-avatar.png";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";


type Props = {
  title: string;
  content: string;
};

const PHONE_WIDTH = 380;
const PHONE_HEIGHT = 640;

export function TemplatePreview({ title, content }: Props) {
  const now = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  return (
    <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
      <MDTypography variant="h6" mb={2}>
        {title}
      </MDTypography>

      <MDBox display="flex" justifyContent="center">
        {/* Device body */}
        <MDBox
          sx={(theme) => ({
            position: "relative",
            width: "100%",
            maxWidth: theme.functions.pxToRem(PHONE_WIDTH + 24),
            p: theme.functions.pxToRem(12),
            borderRadius: theme.functions.pxToRem(44),
            background: "linear-gradient(160deg, #2b3136 0%, #14181b 45%, #2b3136 100%)",
            boxShadow: `${theme.boxShadows.xl}, inset 0 0 ${theme.functions.pxToRem(2)} rgba(255,255,255,0.35)`,

            // Botões laterais
            "&::before, &::after": {
              content: '""',
              position: "absolute",
              right: theme.functions.pxToRem(-3),
              width: theme.functions.pxToRem(3),
              borderRadius: theme.functions.pxToRem(2),
              background: "#3a4045",
            },
            "&::before": {
              top: theme.functions.pxToRem(110),
              height: theme.functions.pxToRem(70),
            },
            "&::after": {
              top: theme.functions.pxToRem(196),
              height: theme.functions.pxToRem(40),
            },
          })}
        >
          {/* Botão de volume (esquerda) */}
          <MDBox
            sx={(theme) => ({
              position: "absolute",
              left: theme.functions.pxToRem(-3),
              top: theme.functions.pxToRem(120),
              width: theme.functions.pxToRem(3),
              height: theme.functions.pxToRem(96),
              borderRadius: theme.functions.pxToRem(2),
              background: "#3a4045",
            })}
          />

          {/* Screen */}
          <MDBox
            sx={(theme) => ({
              position: "relative",
              width: "100%",
              height: theme.functions.pxToRem(PHONE_HEIGHT),

              borderRadius: theme.functions.pxToRem(34),
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              background: "#0b141a",
            })}
          >
            {/* Header */}
            <MDBox
              sx={(theme) => ({
                background: "#075E54",
                px: 1.5,
                pt: 1,

                pb: 1,
                display: "flex",
                alignItems: "center",
                gap: theme.functions.pxToRem(8),
              })}
            >

            <MDBox
              component="img"
              src={talk2buyAvatar}
              alt="Talk2Buy"
              sx={(theme) => ({
                width: theme.functions.pxToRem(32),
                height: theme.functions.pxToRem(32),
                borderRadius: "50%",
                objectFit: "cover",
                background: "rgba(255,255,255,0.25)",
              })}
            />

            <MDBox>
              <MDTypography variant="button" fontWeight="medium" sx={{ color: "#fff" }}>
                Talk2Buy
              </MDTypography>
              <MDTypography variant="caption" display="block" sx={{ color: "#cfe9e5" }}>
                online
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* Conversation */}
          <MDBox
            sx={{
              flex: 1,
              minHeight: 0,
              background: "#0b141a",
              backgroundImage:
                "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
              backgroundSize: "22px 22px, 22px 22px",
              backgroundPosition: "0 0, 11px 11px",
              p: 1.5,
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.35) transparent",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.35)",
                borderRadius: 3,
              },
            }}

          >
            <MDBox display="flex" justifyContent="flex-end">
              <MDBox
                sx={(theme) => ({
                  background: "#005c4b",
                  color: "#fff",
                  borderRadius: theme.functions.pxToRem(10),
                  borderTopRightRadius: 0,
                  p: 1.25,
                  maxWidth: "85%",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "break-word",
                  boxShadow: theme.boxShadows.sm,
                })}
              >
                <MDTypography variant="button" sx={{ color: "#e9edef" }}>
                  {content}
                </MDTypography>

                <MDBox
                  mt={0.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={0.5}
                >
                  <MDTypography variant="caption" sx={{ color: "rgba(233,237,239,0.6)" }}>
                    {now}
                  </MDTypography>
                  <DoneAllIcon sx={{ fontSize: 14, color: "#53bdeb" }} />
                </MDBox>
              </MDBox>
            </MDBox>
            </MDBox>

            {/* Home indicator */}
            <MDBox
              sx={(theme) => ({
                display: "flex",
                justifyContent: "center",
                background: "#0b141a",
                py: theme.functions.pxToRem(8),
              })}
            >
              <MDBox
                sx={(theme) => ({
                  width: theme.functions.pxToRem(120),
                  height: theme.functions.pxToRem(4),
                  borderRadius: theme.functions.pxToRem(4),
                  background: "rgba(255,255,255,0.45)",
                })}
              />
            </MDBox>
          </MDBox>
        </MDBox>

      </MDBox>
    </Card>
  );
}
